import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, useTheme } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { toast } from "react-toastify";
import CustomLabel from "../CustomLabel";
import CustomTextField from "../CustomTextField";
import { COLORS } from "../../assets/theme";
import {
  requestSuperadminOtp,
  verifySuperadminOtp,
} from "../../services/superadminService";
import { getErrorMessage } from "../../utils/apiResponse";

/** The backend allows 5 OTP requests per 5 minutes — keep resends visibly spaced. */
const RESEND_COOLDOWN_SECONDS = 60;

interface SuperadminOtpStepProps {
  /** Whatever the user typed in the login field — verify-otp needs it again. */
  phone: string;
  channel: string;
  message?: string;
  expiresInMinutes: number;
  onBack: () => void;
}

const channelLabel = (channel: string) =>
  channel === "SMS" ? "SMS" : channel === "WHATSAPP" ? "WhatsApp" : channel;

const formatCountdown = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const SuperadminOtpStep: React.FC<SuperadminOtpStepProps> = ({
  phone,
  channel,
  message,
  expiresInMinutes,
  onBack,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [currentChannel, setCurrentChannel] = useState(channel);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [expiresIn, setExpiresIn] = useState(expiresInMinutes * 60);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    if (expiresIn <= 0) return;
    const timer = setInterval(() => setExpiresIn((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [expiresIn]);

  const expired = expiresIn <= 0;

  const helper = useMemo(() => {
    if (error) return error;
    if (expired) return "This code has expired. Request a new one.";
    return `Expires in ${formatCountdown(expiresIn)}`;
  }, [error, expired, expiresIn]);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length !== 6) {
      setError("Enter the 6-digit code.");
      return;
    }
    setVerifying(true);
    try {
      await verifySuperadminOtp(phone, code.trim());
      toast.success("Login successful!", { position: "top-right" });
      navigate("/superadmin/overview", { replace: true });
    } catch (err: any) {
      const msg = getErrorMessage(err, "Could not verify the code.");
      setError(msg);
      toast.error(msg, { position: "top-right", toastId: String(msg) });
    }
    setVerifying(false);
  };

  const handleResend = async () => {
    setResending(true);
    try {
      const result = await requestSuperadminOtp(phone);
      if (result?.channel) setCurrentChannel(result.channel);
      setExpiresIn((result?.expiresInMinutes ?? expiresInMinutes) * 60);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      setCode("");
      setError(undefined);
      toast.success(result?.message || "A new code is on its way.", {
        position: "top-right",
      });
    } catch (err: any) {
      const msg =
        err?.response?.status === 429
          ? "Too many requests. Wait a few minutes before trying again."
          : getErrorMessage(err, "Could not send a new code.");
      toast.error(msg, { position: "top-right", toastId: String(msg) });
    }
    setResending(false);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <CustomLabel
        text="Verify it's you"
        fontWeight={700}
        fontSize="2rem"
        color={COLORS.BlackText}
        sx={{ mb: 2, textAlign: "center", display: "block" }}
      />
      <Box
        sx={{
          borderBottom: `2px solid ${COLORS.BlackText}`,
          mb: 3,
          width: "80%",
          mx: "auto",
        }}
      />
      <CustomLabel
        text={
          message ||
          `We sent a 6-digit code to ${phone} over ${channelLabel(currentChannel)}.`
        }
        fontWeight={400}
        fontSize="15px"
        color={COLORS.BlackText}
        sx={{ mb: 0.5, textAlign: "center", display: "block" }}
      />
      <CustomLabel
        text={`Check your ${channelLabel(currentChannel)} messages.`}
        fontWeight={400}
        fontSize="13px"
        color={COLORS.placeHolderColor}
        sx={{ mb: 3, textAlign: "center", display: "block" }}
      />

      <Box component="form" onSubmit={handleVerify} sx={{ width: "100%" }}>
        <CustomLabel
          text="Verification code"
          fontWeight={400}
          fontSize="16px"
          color={COLORS.BlackText}
          sx={{ mb: 1 }}
        />
        <CustomTextField
          label=""
          value={code}
          bgColor={COLORS.textFieldBackgroundColor}
          onChange={(e) => {
            setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
            setError(undefined);
          }}
          autoFocus
          fullWidth
          sx={{ mb: 2 }}
          error={!!error || expired}
          helperText={helper}
          inputProps={{
            inputMode: "numeric",
            maxLength: 6,
            style: { letterSpacing: "0.5em", fontSize: "1.3rem" },
          }}
        />

        <Button
          variant="contained"
          color="primary"
          type="submit"
          fullWidth
          disabled={verifying || code.length !== 6}
          sx={{
            borderRadius: 30,
            py: 1.5,
            fontWeight: 700,
            fontSize: "1.1rem",
            background: verifying
              ? theme.palette.primary.light
              : theme.palette.primary.main,
            "&:hover": { background: theme.palette.primary.dark },
          }}
        >
          <CustomLabel
            text={verifying ? "Verifying..." : "Verify"}
            fontWeight={700}
            fontSize="1.1rem"
            color="#fff"
          />
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mt: 2.5,
          gap: 1,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ textTransform: "none", color: COLORS.placeHolderColor }}
        >
          Back
        </Button>
        <Button
          onClick={handleResend}
          disabled={resending || cooldown > 0}
          sx={{ textTransform: "none", fontWeight: 600 }}
        >
          {cooldown > 0
            ? `Resend code in ${formatCountdown(cooldown)}`
            : resending
            ? "Sending..."
            : "Resend code"}
        </Button>
      </Box>
    </Box>
  );
};

export default SuperadminOtpStep;
